import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ObservationsSyncResult } from '../../../models/integration.model';
import { TranslocoService } from '@ngneat/transloco';

export type SyncOutcome = 'success' | 'warning' | 'error';

export interface FailedObservationItem {
  /** Project the record belongs to; empty when the backend does not send it */
  projectId: string;
  projectNumber: string;
  projectName: string;
  recordId: string;
  field: string;
  value: any;
  /** Every invalid field of the entry, in order (the first one is `field`) */
  fields: { key: string; value: any }[];
  reason: string;
  message: string;
}

export interface FailedObservationGroup {
  key: string;
  /** Transloco key of the actionable message, null for unknown reasons */
  actionKey: string | null;
  /** Raw backend reason, shown as a secondary line */
  rawReason: string;
  items: FailedObservationItem[];
  /** Items grouped by project (record numbers repeat across projects) */
  projects: { label: string; number: string; name: string; items: FailedObservationItem[] }[];
}

/** "required" in en / ar / ru: the backend validation message follows the user's locale */
const REQUIRED_PATTERN = /required|مطلوب|обязательн/i;

/** Known rejection reasons mapped to actionable messages: [field pattern, translation key] */
const KNOWN_REASONS: { code: string; field: RegExp; actionKey: string }[] = [
  {
    code: 'discipline_missing',
    field: /discipline/i,
    actionKey: 'integration.sync_reason_discipline_missing',
  },
  {
    code: 'status_missing',
    field: /^status$/i,
    actionKey: 'integration.sync_reason_status_missing',
  },
];

/**
 * Observations Sync Results Modal Component
 * Displays detailed results of observations synchronization operations
 * Type-safe handling of observations-specific result data
 */
@Component({
  selector: 'app-observations-sync-results-modal',
  templateUrl: './observations-sync-results-modal.component.html',
  styleUrls: ['./observations-sync-results-modal.component.scss'],
})
export class ObservationsSyncResultsModalComponent implements OnInit {
  // Public properties
  syncResult: ObservationsSyncResult;
  outcome: SyncOutcome;
  /** Percentage with one decimal, floored so rejected records never round up to 100 */
  successRate: number;
  succeededCount = 0;
  totalCount = 0;
  copiedKey: string | null = null;
  failedGroups: FailedObservationGroup[] = [];
  expandedGroups = new Set<string>();

  constructor(
    public dialogRef: MatDialogRef<ObservationsSyncResultsModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { syncResult: ObservationsSyncResult },
    private transloco: TranslocoService,
  ) {
    this.syncResult = data.syncResult;
    this.outcome = this.computeOutcome();
    this.successRate = this.computeSuccessRate();
    this.buildFailedGroups();
    // Groups start collapsed: each header already shows its count and actionable message
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  ngOnInit(): void {
    console.log('Observations Sync Results Modal initialized with result:', this.syncResult);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Check if the run itself failed (as opposed to finishing with rejected records)
   */
  isRunFailed(): boolean {
    return this.syncResult.status === 'failed' || this.syncResult.status === 'error';
  }

  /**
   * Get success rate percentage
   */
  getSuccessRate(): number {
    return this.successRate;
  }

  toggleGroup(key: string): void {
    if (this.expandedGroups.has(key)) {
      this.expandedGroups.delete(key);
    } else {
      this.expandedGroups.add(key);
    }
  }

  /**
   * Copy the records of one project (one per line) so they can be looked up in Unifier
   */
  copyRecords(
    group: FailedObservationGroup,
    project: FailedObservationGroup['projects'][number],
  ): void {
    const text = [project.label, ...project.items.map((item) => item.recordId || '-')]
      .filter(Boolean)
      .join('\n');
    const key = `${group.key}|${project.label}`;
    navigator.clipboard?.writeText(text).then(
      () => {
        this.copiedKey = key;
        setTimeout(() => {
          if (this.copiedKey === key) {
            this.copiedKey = null;
          }
        }, 2000);
      },
      () => undefined,
    );
  }

  trackByKey(_: number, group: FailedObservationGroup): string {
    return group.key;
  }

  /**
   * Check if there are failed observations
   */
  hasFailedObservations(): boolean {
    return this.failedGroups.length > 0;
  }

  /**
   * Close the modal
   */
  onClose(): void {
    this.dialogRef.close(false);
  }

  /**
   * Refresh observations data
   */
  onRefreshObservationsData(): void {
    this.dialogRef.close(true);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------

  private computeOutcome(): SyncOutcome {
    if (this.isRunFailed()) {
      return 'error';
    }
    return (this.syncResult.failed_count || 0) > 0 ? 'warning' : 'success';
  }

  /**
   * Success rate from the real counts, floored to one decimal so a run with rejected records
   * never reads 100%.
   */
  private computeSuccessRate(): number {
    this.succeededCount =
      (this.syncResult.created_count || 0) +
      (this.syncResult.updated_count || 0) +
      (this.syncResult.matched_count || 0);
    this.totalCount = this.succeededCount + (this.syncResult.failed_count || 0);
    return this.totalCount > 0
      ? Math.floor((this.succeededCount / this.totalCount) * 1000) / 10
      : 0;
  }

  /**
   * De-duplicate failed records by (record id, field, reason) and group them by reason
   */
  private buildFailedGroups(): void {
    const raw: any[] = (this.syncResult.failed_observations as any[]) || [];
    const seen = new Set<string>();
    const groups = new Map<string, FailedObservationGroup>();

    for (const entry of raw.flat()) {
      if (!entry) {
        continue;
      }
      const rowData = entry.rowData || {};
      const fields = Object.keys(rowData).map((key) => ({ key, value: rowData[key] }));
      const field = fields[0]?.key || '';
      const reason = String(entry.reason ?? '');
      const message = String(entry.message ?? '');
      const recordId = message.match(/For Observation \(([^)]*)\)/)?.[1] || '';

      const projectId = this.str(entry.project_id ?? entry.project?.id);
      const projectNumber = this.str(entry.project_number ?? entry.project?.number);
      const projectName = this.str(entry.project_name ?? entry.project?.name);

      // Same record number in two projects is two different problems, so the project is part of
      // the key. Without a project (or a record id) we cannot tell repeats from distinct
      // entries, so nothing is dropped.
      if ((projectId || projectNumber) && recordId) {
        const dedupKey = [projectId || projectNumber, recordId, field, reason].join('|');
        if (seen.has(dedupKey)) {
          continue;
        }
        seen.add(dedupKey);
      }

      const known = REQUIRED_PATTERN.test(reason)
        ? KNOWN_REASONS.find((r) => r.field.test(field))
        : undefined;
      const groupKey = known ? known.code : reason;
      if (!groups.has(groupKey)) {
        groups.set(groupKey, {
          key: groupKey,
          actionKey: known ? known.actionKey : null,
          rawReason: reason,
          items: [],
          projects: [],
        });
      }
      groups.get(groupKey).items.push({
        projectId,
        projectNumber,
        projectName,
        recordId,
        field,
        value: rowData[field],
        fields,
        reason,
        message,
      });
    }

    groups.forEach((group) => {
      const byProject = new Map<string, FailedObservationGroup['projects'][number]>();
      group.items.forEach((item) => {
        const label = [item.projectNumber, item.projectName].filter(Boolean).join(' - ');
        if (!byProject.has(label)) {
          byProject.set(label, {
            label,
            number: item.projectNumber,
            name: item.projectName,
            items: [],
          });
        }
        byProject.get(label).items.push(item);
      });
      group.projects = Array.from(byProject.values());
    });

    this.failedGroups = Array.from(groups.values()).sort((a, b) => b.items.length - a.items.length);
  }

  private str(value: any): string {
    return value === null || value === undefined ? '' : String(value);
  }
}
